import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getApiUrl } from '../App';

const formatHistoryDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ScoreHistoryChart = ({ symbol }) => {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    setHistory(null);
    fetch(getApiUrl(`/api/value-score/${symbol}/history?days=180`))
      .then(r => r.ok ? r.json() : null)
      .then(data => setHistory(data ? data.history : []))
      .catch(() => setHistory([]));
  }, [symbol]);

  if (history === null) return null;
  if (history.length < 2) {
    return (
      <div style={{ marginTop: '14px', fontSize: '12px', color: '#999' }}>
        Score history is not available yet.
      </div>
    );
  }

  const chartData = history.map(h => ({ date: formatHistoryDate(h.date), score: h.valueScore }));
  const spanDays = Math.round(
    (new Date(history[history.length - 1].date) - new Date(history[0].date)) / 86400000
  ) + 1;

  return (
    <div style={{ marginTop: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>
        Score history (last {spanDays} day{spanDays === 1 ? '' : 's'})
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} minTickGap={30} />
          <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={{ fontSize: 11, fill: '#999' }} width={28} />
          <Tooltip
            formatter={(value) => [value.toFixed(1), 'Value score']}
            labelFormatter={(label) => label}
            contentStyle={{ fontSize: '12px', padding: '4px 8px' }}
          />
          <Line
            type="monotone"
            dataKey="score"
            stroke="#3498db"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const BAR_COLOR = (score) => {
  if (score >= 4.5) return '#1a7a4a';
  if (score >= 3.5) return '#27ae60';
  if (score >= 2.5) return '#f39c12';
  if (score >= 1.5) return '#e67e22';
  return '#e74c3c';
};

const ComponentBar = ({ label, score, weight }) => (
  <div style={{ marginBottom: '10px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginBottom: '3px' }}>
      <span>{label} <span style={{ color: '#aaa' }}>({Math.round(weight * 100)}% weight)</span></span>
      <span style={{ fontWeight: '600', color: BAR_COLOR(score) }}>{score.toFixed(1)} / 5</span>
    </div>
    <div style={{ height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: `${(score / 5) * 100}%`,
        background: BAR_COLOR(score),
        borderRadius: '3px',
        transition: 'width 0.6s ease',
      }} />
    </div>
  </div>
);

const ValueScore = ({ symbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetch(getApiUrl(`/api/value-score/${symbol}`))
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) return (
    <div className="value-score-widget value-score-loading">
      <span>Calculating value score…</span>
    </div>
  );

  if (!data) return null;

  const { valueScore, label, color, rating, components } = data;
  const ratingColor = rating === 'Buy' ? '#27ae60' : rating === 'Sell' ? '#e74c3c' : '#e67e22';
  const fillPct = (valueScore / 5) * 100;

  return (
    <div className="value-score-widget">
      <div className="value-score-main">
        {/* Dial / score */}
        <div className="value-score-dial">
          <svg viewBox="0 0 120 70" width="120" height="70">
            {/* Background arc */}
            <path d="M 10 65 A 50 50 0 0 1 110 65" fill="none" stroke="#eee" strokeWidth="10" strokeLinecap="round" />
            {/* Colored arc — length is 157 (half circumference of r=50) */}
            <path
              d="M 10 65 A 50 50 0 0 1 110 65"
              fill="none"
              stroke={color}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${(fillPct / 100) * 157} 157`}
            />
            <text x="60" y="62" textAnchor="middle" fontSize="22" fontWeight="700" fill={color}>
              {valueScore.toFixed(1)}
            </text>
          </svg>
          <div style={{ textAlign: 'center', fontSize: '11px', color: '#999', marginTop: '-6px' }}>out of 5</div>
        </div>

        {/* Label + breakdown toggle */}
        <div className="value-score-label">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color }}>{label}</div>
            {rating && (
              <div style={{
                fontSize: '13px', fontWeight: '700', color: '#fff',
                background: ratingColor, borderRadius: '4px',
                padding: '2px 10px', letterSpacing: '0.05em',
              }}>{rating}</div>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Value Score</div>
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#3498db',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {expanded ? 'Hide breakdown ▲' : 'Show breakdown ▼'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="value-score-breakdown">
          <ComponentBar label="Growth (implied vs actual)" score={components.growth.score} weight={components.growth.weight} />
          <ComponentBar label="Debt position (liabilities/assets)" score={components.debt.score} weight={components.debt.weight} />
          <ComponentBar label="Cash on hand (vs 6 months opex)" score={components.cash.score} weight={components.cash.weight} />

          <div style={{ marginTop: '12px', fontSize: '12px', color: '#aaa', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
            <div>
              <div style={{ fontWeight: '600', color: '#666' }}>Implied growth</div>
              <div>{components.growth.impliedGrowth != null ? `${components.growth.impliedGrowth}%` : 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#666' }}>Historical growth</div>
              <div>{components.growth.historicalGrowth != null ? `${components.growth.historicalGrowth.toFixed(1)}%` : 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#666' }}>Analyst forward est.</div>
              <div>{components.growth.forwardGrowth != null ? `${components.growth.forwardGrowth.toFixed(1)}%` : 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#666' }}>Blended growth</div>
              <div>{components.growth.blendedGrowth != null ? `${components.growth.blendedGrowth.toFixed(1)}%` : 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#666' }}>Debt / assets</div>
              <div>{components.debt.debtToAssets != null ? `${(components.debt.debtToAssets * 100).toFixed(1)}%` : 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#666' }}>Cash coverage</div>
              <div>{`${components.cash.cashCoverageMonths} months`}</div>
            </div>
          </div>

          <ScoreHistoryChart symbol={symbol} />
        </div>
      )}
    </div>
  );
};

export default ValueScore;
